import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import {
  apiAdminGetAgentMembers,
  apiAdminGetMasterAgents,
  ApiRequestError,
  type AdminMember,
} from '@/lib/api'
import type { PaginatedResult } from '@nexbit/types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { ArrowLeft, AlertCircle, RefreshCw, Lock } from 'lucide-react'
import { useT } from '@/contexts/language'

function statusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (s === 'active') return 'success'
  if (s === 'suspended') return 'destructive'
  if (s === 'inactive') return 'warning'
  return 'outline'
}

export default function AdminAgentMembersPage() {
  const t = useT()
  const navigate = useNavigate()
  const { masterId, agentId } = useParams<{ masterId: string; agentId: string }>()
  const [members, setMembers] = useState<PaginatedResult<AdminMember> | null>(null)
  const [agentName, setAgentName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!agentId || !masterId) return
    setLoading(true)
    setError(null)
    try {
      const [mem, agents] = await Promise.all([
        apiAdminGetAgentMembers(agentId, 1, 50),
        apiAdminGetMasterAgents(masterId),
      ])
      setMembers(mem)
      setAgentName(agents.find((a) => a.id === agentId)?.name ?? agentId)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [agentId, masterId, t.common.error])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/masters/${masterId}/agents`)}
          className="h-8 gap-1.5 font-mono text-xs shrink-0"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          เอเจนต์
        </Button>
        <div className="min-w-0 flex-1 flex items-center gap-2">
          {loading ? (
            <Skeleton className="h-5 w-40" />
          ) : (
            <>
              <h1 className="font-mono text-sm font-bold uppercase tracking-wider text-foreground truncate">
                {agentName}
              </h1>
              <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
                <Lock className="h-3 w-3" /> ดูอย่างเดียว
              </span>
            </>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void load()}
          disabled={loading}
          className="h-8 gap-1.5 font-mono text-xs shrink-0"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {t.common.refresh}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            สมาชิกในสายนี้
          </span>
          {members && !loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">{members.total} รายการ</span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-5 w-14" />
                <Skeleton className="ml-auto h-4 w-20" />
              </div>
            ))}
          </div>
        ) : !members || members.items.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">{t.members.noMembers}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.common.username}</TableHead>
                <TableHead>{t.common.status}</TableHead>
                <TableHead className="text-right">{t.common.createdShort}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.items.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <p className="font-mono text-sm font-semibold">{m.username}</p>
                    {m.displayName && <p className="font-mono text-xs text-muted-foreground">{m.displayName}</p>}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(m.status)} className="text-[10px]">
                      {m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <time dateTime={m.createdAt} className="font-mono text-xs text-muted-foreground">
                      {format(new Date(m.createdAt), 'dd/MM/yy')}
                    </time>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
